import { Account } from '@/types/planning';
import { useSelector } from 'react-redux';
import { ReduxState } from '@/redux/modules/types';
import PortfolioRiskProfile from '@/types/portfolio-risk-profile';
import { getRiskProfilesWithQuestionnaireContext } from '@/utils/risk-profiles';

/**
 * Custom hook to reconcile an account's risk profile with the active questionnaire.
 *
 * @returns An object containing the `getAccountRiskProfile` function.
 *
 * @example
 * const { getAccountRiskProfile } = useReconcileAccountRiskProfile();
 * const riskProfile = getAccountRiskProfile(account);
 *
 *
 * @property {Function} getAccountRiskProfile - Function to get the risk profile of a given account.
 * @param {Account} [account] - The account for which to get the risk profile.
 * @returns {Partial<PortfolioRiskProfile> | undefined} The risk profile of the account if it matches the active questionnaire, otherwise undefined.
 */
const useReconcileAccountRiskProfile = (): { getAccountRiskProfile: (account?: Account) => Partial<PortfolioRiskProfile> | undefined } => {
  const questionnaires = useSelector((state: ReduxState) => state.kycQuestionnaire.questionnaires);
  const riskProfiles = getRiskProfilesWithQuestionnaireContext(questionnaires);

  const getAccountRiskProfile = (account?: Account) => {
    if (!account?.riskProfile) {
      return undefined;
    }

    const activeRiskProfile = riskProfiles?.find(riskProfile => riskProfile.profile._id === account.riskProfile?._id);

    if (!activeRiskProfile) {
      return undefined;
    }

    return {
      profile: activeRiskProfile.profile,
      questionnaire: activeRiskProfile.questionnaireId,
      savedSections: {},
    };
  };

  return { getAccountRiskProfile };
};

export default useReconcileAccountRiskProfile;
